<script lang="ts">
  // Post-login gateway, Master Duel cadence: a big typographic menu on the left
  // (WORLD / FREE DUEL, a diamond cursor + accent bar mark the hot entry), the
  // ace monster (Cyber End Dragon) as the hero on the right — its glow regrades
  // to the hot mode's accent, so the screen reacts to the cursor — and a
  // briefing panel that swaps with it. World keeps the structural cyan; Free
  // Duel takes the magenta counter-accent.
  import { fade, fly } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { session, chooseMode, go } from "../stores/session.svelte.ts";
  import { isPhone } from "../device.svelte.ts";
  import { logout as netLogout } from "../stores/net.svelte.ts";
  import { watchReplay } from "../stores/duel.svelte.ts";
  import { chibiFrom } from "../../render/ChibiAvatar.ts";
  import { createHotkey } from "../hotkeys.ts";
  import PortraitPreview from "../components/PortraitPreview.svelte";
  import HeroDragon from "../components/HeroDragon.svelte";
  import type { DuelFormat } from "@ygoevo/world-protocol";

  const name = $derived(session.loginOk?.avatar.name ?? "Duelist");
  const dp = $derived(session.loginOk?.wallet.dp ?? 0);
  const look = $derived(chibiFrom(session.loginOk?.avatar));

  const reduced =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  const d = (ms: number) => (reduced ? 0 : ms);

  function pick(mode: DuelFormat) {
    chooseMode(mode);
  }
  function signOut() {
    netLogout();
    go("title");
  }


  // 1 / 2 jump straight into a mode (the entries are real buttons, so Tab + Enter already work).
  createHotkey("1", () => pick("world"));
  createHotkey("2", () => pick("free"));

  type Accent = "cyan" | "magenta";
  interface Mode {
    id: DuelFormat;
    accent: Accent;
    title: string;
    tagline: string;
    blurb: string;
    features: string[];
    key: string;
  }
  const modes: Mode[] = [
    {
      id: "world",
      accent: "cyan",
      title: "World",
      tagline: "The full collectible online world.",
      blurb: "Live in Domino City and Duel Academy. Walk the overworld, collect cards, and climb the ladder.",
      features: ["Walk the overworld & city lobbies", "Open packs, craft & trade cards", "Ranked ladder, NPCs & progression"],
      key: "1",
    },
    {
      id: "free",
      accent: "magenta",
      title: "Free Duel",
      tagline: "Every card unlocked. Straight to the duel.",
      blurb: "Pure dueling, no overworld. Every card is unlocked and the latest Master Rules apply, with the Extra Monster (Link) Zones in play.",
      features: ["Every card unlocked, build anything", "Latest rules with Link / Extra Monster Zones", "Single, Match & Ranked. Quick or host"],
      key: "2",
    },
  ];

  // The menu cursor: which entry is hot (hover / focus). Defaults to World and
  // always points at something, so the briefing panel never goes empty.
  let hovered = $state<DuelFormat>("world");
  const active = $derived(modes.find((m) => m.id === hovered) ?? modes[0]!);

  const accentVar = (a: Accent) => (a === "magenta" ? "var(--color-magenta)" : "var(--color-cyan)");
</script>

<div class="relative h-full w-full overflow-hidden">
  <!-- ── Cyber End Dragon: the GX ace spreading its wings over the blue zone.
       Its aura/glow follow the hot menu entry — the hero reacts to the player. -->
  <div in:fade={{ duration: d(900), delay: d(150) }}>
    <HeroDragon accent={active.accent === "magenta" ? "magenta" : "blue"} />
  </div>

  <!-- Identity + sign-out bar -->
  <div
    class="pointer-events-auto absolute inset-x-4 top-4 z-20 flex items-center justify-between sm:inset-x-8 sm:top-6"
    in:fade={{ duration: d(400), delay: d(120) }}
  >
    <div class="flex items-center gap-2.5">
      <div class="grid size-9 place-items-center overflow-hidden chamfer bg-void-2 ring-1 ring-cyan/25" style="--c:7px">
        <PortraitPreview size={36} {look} headshot />
      </div>
      <div class="leading-tight">
        <div class="font-display text-sm font-bold tracking-wide text-ink">{name}</div>
        <div class="flex items-baseline gap-1">
          <span class="font-display text-[10px] uppercase tracking-[0.14em] text-gold/80">DP</span>
          <span class="font-display text-xs font-bold tabular-nums text-gold">{dp.toLocaleString()}</span>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-5">
      <button
        onclick={() => void watchReplay("mode")}
        class="min-h-11 px-2 font-display text-[0.66rem] uppercase tracking-[0.18em] text-ink-dim/65 transition-colors hover:text-cyan"
      >
        Watch Replay
      </button>
      <button
        onclick={signOut}
        class="min-h-11 px-2 font-display text-[0.66rem] uppercase tracking-[0.18em] text-ink-dim/65 transition-colors hover:text-magenta"
      >
        Sign out
      </button>
    </div>
  </div>

  <!-- ── Command column ── -->
  <div
    class="relative z-10 flex h-full max-w-[46rem] flex-col justify-center gap-[clamp(1.6rem,5vh,3rem)] px-[clamp(1.5rem,6vw,6.5rem)] pt-10 phone:justify-start phone:gap-3 phone:overflow-y-auto phone:overflow-x-hidden phone:pb-4 phone:pt-14 scroll-cyber"
  >
    <header class="flex items-center gap-3" in:fly={{ y: 14, duration: d(500), delay: d(140), easing: expoOut }}>
      <span class="size-1.5 rotate-45 bg-cyan" style="box-shadow: 0 0 8px var(--color-cyan)"></span>
      <h1 class="font-display text-xs font-semibold uppercase tracking-[0.42em] text-cyan">Select Mode</h1>
      <span class="h-px w-24 bg-gradient-to-r from-cyan/50 to-transparent" aria-hidden="true"></span>
    </header>

    <!-- ── Gate panels: each mode is a physical gate, not a floating word.
         Dark chamfered surface (text always reads), accent edge that charges
         when hot, an energy current flowing through it, a mode glyph watermark,
         and a briefing that unfolds inside the gate itself. -->
    <nav class="flex flex-col gap-[clamp(0.8rem,2.6vh,1.4rem)]" aria-label="Game modes">
      {#each modes as m, i (m.id)}
        {@const on = hovered === m.id}
        {@const ac = accentVar(m.accent)}
        <button
          type="button"
          onclick={() => pick(m.id)}
          onpointerenter={() => (hovered = m.id)}
          onfocus={() => (hovered = m.id)}
          class="group w-[min(37rem,88vw)] text-left"
          in:fly={{ x: -26, duration: d(560), delay: d(220 + i * 100), easing: expoOut }}
        >
          <!-- edge: p-px double-clip; charges from dim to a live gradient. No
               transform on hover — the gate stays put (width + position fixed);
               only the edge, glow and inner briefing change. -->
          <span
            class="chamfer block p-px transition-[filter,background-color] duration-300 ease-[var(--ease-out-quint)]"
            style="--c:15px; background: {on
              ? `linear-gradient(115deg, ${ac}, color-mix(in oklab, ${ac} 30%, transparent) 60%, ${ac})`
              : `color-mix(in oklab, ${ac} 26%, transparent)`}; filter: {on
              ? `drop-shadow(0 0 14px color-mix(in oklab, ${ac} 40%, transparent))`
              : 'none'}"
          >
            <span class="chamfer relative block overflow-hidden bg-surface/95 px-6 py-4" style="--c:14px">
              <!-- interior texture + energy current (hot only) -->
              <span class="grid-tex absolute inset-0 opacity-35" aria-hidden="true"></span>
              {#if on}
                <span class="gate-energy absolute inset-0" style="--ga:{ac}" aria-hidden="true"></span>
              {/if}
              <!-- mode glyph watermark: World = the grid lattice, Free Duel = the clash -->
              <svg
                viewBox="0 0 24 24"
                class="pointer-events-none absolute -right-2 top-1/2 size-28 -translate-y-1/2 transition-opacity duration-300 {on ? 'opacity-[0.16]' : 'opacity-[0.07]'}"
                fill="none"
                stroke={ac}
                stroke-width="1"
                aria-hidden="true"
              >
                {#if m.id === "world"}
                  <path d="M12 2l8.66 5v10L12 22l-8.66-5V7L12 2z" />
                  <path d="M12 2v20M3.34 7l17.32 10M20.66 7L3.34 17" opacity="0.5" />
                  <path d="M12 10.5l1.5 1.5-1.5 1.5-1.5-1.5z" fill={ac} stroke="none" />
                {:else}
                  <path d="M4.5 5.5l8 6.5-8 6.5z" />
                  <path d="M19.5 5.5l-8 6.5 8 6.5z" opacity="0.7" />
                {/if}
              </svg>

              <span class="relative flex items-center gap-3.5">
                <!-- menu cursor -->
                <span
                  class="size-2.5 shrink-0 rotate-45 transition-all duration-200 ease-[var(--ease-out-quint)] {on
                    ? 'scale-100 opacity-100'
                    : 'scale-0 opacity-0'}"
                  style="background:{ac}; box-shadow: 0 0 12px {ac}"
                  aria-hidden="true"
                ></span>
                <span
                  class="font-display text-[clamp(1.9rem,3.4vw,2.6rem)] font-bold uppercase leading-none tracking-[0.06em] transition-all duration-200 {on
                    ? 'text-ink'
                    : 'text-ink/80'}"
                  style={on ? `text-shadow: 0 0 20px color-mix(in oklab, ${ac} 60%, transparent)` : ""}
                >{m.title}</span>
                <!-- key chip: p-px double-clip so the accent edge survives the chamfer -->
                <span
                  class="ml-auto hidden shrink-0 chamfer-btn p-px transition-colors duration-200 sm:block touch:hidden"
                  style="--c:5px; background: color-mix(in oklab, {ac} {on ? 65 : 30}%, transparent)"
                >
                  <kbd
                    class="grid size-6 place-items-center chamfer-btn bg-void-2 font-display text-xs font-bold transition-colors duration-200 {on
                      ? m.accent === 'magenta'
                        ? 'text-magenta'
                        : 'text-cyan'
                      : 'text-ink-dim/80'}"
                    style="--c:4px"
                    title="Press {m.key} to enter"
                  >{m.key}</kbd>
                </span>
              </span>
              <span class="relative mt-1.5 block text-sm leading-snug text-ink-dim">{m.tagline}</span>

              <!-- the briefing unfolds inside the gate — desktop-only courtship: on
                   phones a tap enters the mode immediately, so it stays folded
                   (title + tagline carry the choice) and both gates fit the viewport -->
              <span
                class="relative grid transition-[grid-template-rows] duration-300 ease-[var(--ease-out-quint)]"
                style="grid-template-rows: {on && !isPhone() ? '1fr' : '0fr'}"
              >
                <span class="block overflow-hidden">
                  <span
                    class="mt-3 block h-px w-full"
                    style="background: linear-gradient(90deg, color-mix(in oklab, {ac} 55%, transparent), transparent)"
                    aria-hidden="true"
                  ></span>
                  <span class="mt-3 block max-w-[52ch] text-sm leading-relaxed text-ink-dim">{m.blurb}</span>
                  <span class="mb-1 mt-2.5 block space-y-1.5">
                    {#each m.features as f (f)}
                      <span class="flex items-start gap-2.5">
                        <span
                          class="mt-1.5 size-1.5 shrink-0 rotate-45"
                          style="background:{ac}; box-shadow: 0 0 5px {ac}"
                          aria-hidden="true"
                        ></span>
                        <span class="text-[0.8rem] leading-snug text-ink/90">{f}</span>
                      </span>
                    {/each}
                  </span>
                </span>
              </span>
            </span>
          </span>
        </button>
      {/each}
    </nav>
  </div>
</div>

<style>
  /* A slow energy current flowing through the hot gate. */
  .gate-energy {
    background: linear-gradient(
      105deg,
      transparent 35%,
      color-mix(in oklab, var(--ga) 14%, transparent) 50%,
      transparent 65%
    );
    background-size: 260% 100%;
    animation: gate-flow 3.2s linear infinite;
  }
  @keyframes gate-flow {
    from {
      background-position: 130% 0;
    }
    to {
      background-position: -130% 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .gate-energy {
      animation: none;
      background-position: 50% 0;
    }
  }
</style>
